import { Trans } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import IconExternalLink from '@/icons/ExternalLink.svg';
import IconPackage from '@/icons/Package.svg';
import IconTruck from '@/icons/Truck.svg';
import {
  canBeCreatedBy,
  SHIPMENT_CARRIERS,
  ShipmentWithRelations,
  useCarrierName,
} from '@/models/shipment';
import { ShipmentMessageTitle } from '@/routes/Shipments/Shipment/components/ShipmentStep/components/ShipmentMessageTitle';
import { useCurrentSession } from '@/services/auth';
import { formatDate } from '@/utils/date';

const HandoverMessage = ({
  shipment,
  hideIcon,
  className,
}: {
  shipment: ShipmentWithRelations;
  hideIcon?: boolean;
  className?: string;
}) => {
  const { currentSession } = useCurrentSession();

  const carrierName = useCarrierName(shipment);
  const carrier = SHIPMENT_CARRIERS.find(({ id }) => id === shipment?.carrier);

  const canCreateShipment = canBeCreatedBy(shipment, currentSession);

  const pickupDate = shipment.pickupDate
    ? formatDate(parseISO(shipment.pickupDate), {})
    : undefined;

  if (!canCreateShipment) {
    if (shipment.status === 'pending-pickup') {
      return (
        <AlertBar
          type="info"
          size="large"
          icon={<IconPackage />}
          hideIcon={hideIcon}
          className={className}
          title={
            <ShipmentMessageTitle shipment={shipment}>
              <Trans id="requests.transit.transit-pending-message.pending-pickup.title">
                Waiting for carrier pickup
              </Trans>
            </ShipmentMessageTitle>
          }
        >
          {shipment.handover === 'automatic_pickup' && (
            <p>
              <Trans id="shipment.handover-message.automatic-pickup.text">
                The carrier will pick up the package at the next pre-agreed slot.
              </Trans>
            </p>
          )}
          {shipment.handover === 'pickup' && !!pickupDate && (
            <p>
              <Trans id="requests.transit.transit-pending-message.pending-pickup.text">
                The pickup is scheduled for {pickupDate}
              </Trans>
            </p>
          )}
        </AlertBar>
      );
    }

    if (shipment.status === 'pending-dropoff') {
      const originName = shipment.originEntity?.name;

      return (
        <AlertBar
          type="info"
          size="large"
          icon={<IconPackage />}
          hideIcon={hideIcon}
          className={className}
          title={
            <ShipmentMessageTitle shipment={shipment}>
              <Trans id="requests.transit.transit-pending-message.dropoff.title">
                Waiting for carrier drop-off
              </Trans>
            </ShipmentMessageTitle>
          }
        >
          {!!originName && (
            <p>
              <Trans id="requests.transit.transit-pending-message.dropoff.text-entity">
                {originName} will drop the package off at the nearest {carrierName} pickup point.
              </Trans>
            </p>
          )}
        </AlertBar>
      );
    }
  }

  if (shipment.status === 'pending-pickup') {
    return (
      <AlertBar
        type="info"
        size="large"
        icon={<IconTruck />}
        hideIcon={hideIcon}
        className={className}
        title={
          <ShipmentMessageTitle shipment={shipment}>
            <Trans id="shipment.handover-message.pickup.title">Waiting for carrier pickup</Trans>
          </ShipmentMessageTitle>
        }
      >
        {shipment.handover === 'automatic_pickup' && (
          <p>
            <Trans id="shipment.handover-message.automatic-pickup.text">
              The carrier will pick up the package at the next pre-agreed slot.
            </Trans>
          </p>
        )}
        {shipment.handover === 'pickup' && !!pickupDate && (
          <p>
            <Trans id="requests.transit.transit-pending-message.pending-pickup.text">
              The pickup is scheduled for {pickupDate}
            </Trans>
          </p>
        )}
      </AlertBar>
    );
  }

  if (shipment.status === 'pending-dropoff') {
    return (
      <AlertBar
        type="warning"
        size="large"
        icon={<IconPackage />}
        hideIcon={hideIcon}
        className={className}
        title={
          <ShipmentMessageTitle shipment={shipment}>
            <Trans id="shipment.handover-message.dropoff.title">Drop the package</Trans>
          </ShipmentMessageTitle>
        }
      >
        <Stack gap="8px">
          <p>
            <Trans id="shipment.handover-message.dropoff.text">
              Go to the nearest {carrierName} pickup point and drop the package
            </Trans>
          </p>
          {!!carrier && !!carrier.dropoffPoints && (
            <div>
              <Button
                variant="secondary"
                size="medium"
                href={carrier.dropoffPoints}
                target="_blank"
              >
                <Trans id="shipment.handover-message.dropoff.drop-off-points">
                  View drop-off points
                </Trans>
                <IconExternalLink />
              </Button>
            </div>
          )}
        </Stack>
      </AlertBar>
    );
  }
};

export default HandoverMessage;
